import { useState, useCallback } from 'react';

import { toCarouselMedia } from '../components/modals/AttachmentCarouselModal';
import { SavedAttachment } from '../services/attachmentService';
import { isCarouselSupported } from '../utils/attachmentCarousel';

import type { CarouselMedia } from '../components/modals/AttachmentCarouselModal';

export interface AttachmentCarouselState {
  visible: boolean;
  /** Request attachments as carousel items (`toCarouselMedia`, GitLab #1539). */
  items: CarouselMedia[];
  initialIndex: number;
  sectionLabel: string;
}

export interface UseAttachmentCarouselReturn {
  carouselProps: {
    visible: boolean;
    onClose: () => void;
    items: CarouselMedia[];
    initialIndex: number;
    sectionLabel: string;
  };
  openCarousel: (
    tapped: SavedAttachment,
    filterSource: SavedAttachment[],
    sectionLabel: string,
  ) => void;
}

export const useAttachmentCarousel = (): UseAttachmentCarouselReturn => {
  const [state, setState] = useState<AttachmentCarouselState>({
    visible: false,
    items: [],
    initialIndex: 0,
    sectionLabel: '',
  });

  const openCarousel = useCallback(
    (
      tapped: SavedAttachment,
      filterSource: SavedAttachment[],
      sectionLabel: string,
    ) => {
      const supportedItems = filterSource.filter(a =>
        isCarouselSupported(a.mimeType),
      );
      const idx = supportedItems.findIndex(a => a.id === tapped.id);
      setState({
        visible: true,
        items: supportedItems.map(toCarouselMedia),
        initialIndex: idx >= 0 ? idx : 0,
        sectionLabel,
      });
    },
    [],
  );

  const closeCarousel = useCallback(() => {
    setState(prev => ({ ...prev, visible: false }));
  }, []);

  return {
    carouselProps: {
      visible: state.visible,
      onClose: closeCarousel,
      items: state.items,
      initialIndex: state.initialIndex,
      sectionLabel: state.sectionLabel,
    },
    openCarousel,
  };
};
